import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, KeyRound, ShieldCheck } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { useAuth, formatApiError } from '../context/AuthContext';

/**
 * AuthSheet — bottom-sheet auth surface that hosts sign-in + register on the
 * Home page so visitors don't navigate away from the landing for the common
 * auth path. Email-link destination flows (forgot/reset/verify/signin-code)
 * stay as standalone routes since they're hit via inbound URLs.
 *
 * Modes: 'signin' | 'register' | null (closed)
 *
 * Driven by route — App.js renders <Home authView="signin" /> for /login and
 * <Home authView="register" /> for /register, which forwards to this sheet.
 * The sheet itself handles toggling between modes via internal state.
 *
 * Responsive: shadcn Sheet with side="bottom". On desktop the content is
 * centered horizontally via a max-w constraint; on mobile it anchors full-
 * width to the bottom of the viewport — same component, same animation.
 */
const AuthSheet = ({ mode, onClose }) => {
  const [internalMode, setInternalMode] = useState(mode || 'signin');

  useEffect(() => {
    if (mode) setInternalMode(mode);
  }, [mode]);

  const open = !!mode;
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="bottom"
        className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-h-[92vh] overflow-y-auto sm:max-w-md sm:mx-auto sm:rounded-t-xl"
        data-testid={`auth-sheet-${internalMode}`}
      >
        <SheetHeader>
          <SheetTitle className="font-display text-3xl tracking-wide leading-none text-enf-text-primary">
            {internalMode === 'signin' ? 'Sign in' : 'Claim your spot'}
          </SheetTitle>
        </SheetHeader>

        {internalMode === 'signin' ? (
          <SignInForm onSwitchToRegister={() => setInternalMode('register')} onSuccess={onClose} />
        ) : (
          <RegisterForm onSwitchToSignin={() => setInternalMode('signin')} onSuccess={onClose} />
        )}
      </SheetContent>
    </Sheet>
  );
};

// ----- Sign In form -----------------------------------------------------------

const SignInForm = ({ onSwitchToRegister, onSuccess }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // If AuthGated bounced an unauthed user here with `state.from`, we honor
  // it post-sign-in. Otherwise default to /progress.
  const redirectTo = location.state?.from || '/progress';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email.trim().toLowerCase(), password);
      onSuccess();
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-4" data-testid="signin-form">
      <label className="block">
        <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Email</span>
        <input
          type="email" required autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
          data-testid="signin-email"
        />
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Password</span>
        <div className="relative">
          <input
            type={showPw ? 'text' : 'password'} required autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2.5 pr-10 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
            data-testid="signin-password"
          />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-enf-text-muted hover:text-enf-text-primary p-1" aria-label={showPw ? 'Hide password' : 'Show password'}>
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </label>

      {error && (
        <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md text-sm text-enf-role-danger" data-testid="signin-error">{error}</div>
      )}

      <button
        type="submit" disabled={busy}
        className="w-full py-3 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
        data-testid="signin-submit"
      >
        {busy ? 'Lighting up...' : <>Sign in <ArrowRight className="w-4 h-4" /></>}
      </button>

      <div className="pt-2 text-center text-sm text-enf-text-muted">
        No account yet?{' '}
        <button type="button" onClick={onSwitchToRegister} className="text-enf-role-info hover:text-enf-text-primary font-semibold" data-testid="signin-switch-register">
          Use your invite code
        </button>
      </div>

      <div className="text-center text-sm text-enf-text-muted">
        <a href="/forgot-password" className="text-enf-role-info hover:text-enf-text-primary font-semibold" data-testid="signin-forgot-password-link">Forgot your password?</a>
      </div>

      <div className="text-center text-sm text-enf-text-muted pb-2">
        <a href="/sign-in-code" className="text-enf-role-info hover:text-enf-text-primary font-semibold" data-testid="signin-code-link">Or sign in with a 6-digit code</a>
      </div>
    </form>
  );
};

// ----- Register form ---------------------------------------------------------

const RegisterForm = ({ onSwitchToSignin, onSuccess }) => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState((params.get('invite') || '').toUpperCase());
  const [showPw, setShowPw] = useState(false);
  const [agreedBeta, setAgreedBeta] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!agreedBeta) {
      setError('Please acknowledge the beta participation note to continue.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await register({
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
        inviteCode: inviteCode.trim().toUpperCase(),
      });
      // Record beta-terms acceptance locally; ProfileContext will sync it to
      // the backend on its next profile PUT.
      try {
        localStorage.setItem('enferno_beta_terms_accepted_at', new Date().toISOString());
      } catch { /* ignore — storage quota / private mode */ }
      onSuccess();
      navigate('/progress', { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-4" data-testid="register-form">
      <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold flex items-center gap-2">
        <KeyRound className="w-3.5 h-3.5" /> Private beta access
      </div>
      <p className="text-enf-text-secondary text-sm">
        Enferno is invite-only right now. Punch in your code, set a password, and start training.
      </p>

      <label className="block">
        <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Invite code</span>
        <input
          type="text" required value={inviteCode}
          onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
          placeholder="Enter your invite code"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          className="w-full px-3 py-2.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary font-mono tracking-wider focus:outline-none focus:border-enf-accent-fg"
          data-testid="register-invite"
        />
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Name (optional)</span>
        <input
          type="text" autoComplete="name" value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
          data-testid="register-name"
        />
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Email</span>
        <input
          type="email" required autoComplete="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2.5 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
          data-testid="register-email"
        />
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Password (min 8 chars)</span>
        <div className="relative">
          <input
            type={showPw ? 'text' : 'password'} required autoComplete="new-password" minLength={8} value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2.5 pr-10 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
            data-testid="register-password"
          />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-enf-text-muted hover:text-enf-text-primary p-1" aria-label={showPw ? 'Hide password' : 'Show password'}>
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </label>

      <div className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedBeta}
            onChange={(e) => setAgreedBeta(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-enf-accent flex-shrink-0"
            data-testid="register-beta-agree"
            required
          />
          <span className="text-xs text-enf-text-secondary leading-relaxed">
            <span className="inline-flex items-center gap-1.5 text-enf-role-info font-semibold uppercase tracking-widest mr-1">
              <ShieldCheck className="w-3 h-3" /> Beta participant
            </span>
            I understand Enferno is pre-release software. I'll keep premium and unreleased feature previews to my trusted circle until public launch, and report bugs through the in-app feedback channel. Enferno may use my anonymized session data in product development.
          </span>
        </label>
      </div>

      {error && (
        <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md text-sm text-enf-role-danger" data-testid="register-error">{error}</div>
      )}

      <button
        type="submit" disabled={busy}
        className="w-full py-3 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
        data-testid="register-submit"
      >
        {busy ? 'Lighting up...' : <>Bring the heat <ArrowRight className="w-4 h-4" /></>}
      </button>

      <div className="pt-2 text-center text-sm text-enf-text-muted pb-2">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToSignin} className="text-enf-role-info hover:text-enf-text-primary font-semibold" data-testid="register-switch-signin">
          Sign in
        </button>
      </div>
    </form>
  );
};

export default AuthSheet;
